// 🔐 28/09/2026 (3DS, etapa 2): os dados de cobrança e as contas do 3DS, sem tela e sem
// rede, para dar para testar tudo (cobranca.test.js). Quem usa: services/tresDS.js,
// components/CamposCobranca e as duas telas de pagamento (Register e Dashboard/Sign).
//
// Decisão do dono (28/09/2026): CEP para todos que pagam com cartão; CPF depois.
// A Elo exige mais (manual oficial da Cielo, desde 15/05/2026): e-mail, telefone, rua e
// número e o endereço de internet do aparelho. Por isso só o cartão Elo pede celular e endereço.

export const soDigitos = texto => `${texto ?? ''}`.replace(/\D/g, '');

// Faixas do cartão Elo (as mesmas que GetCardType lista em utils/index.js).
// 🪤 Elas começam com 4, 5 e 6: conferir a Elo ANTES de Visa (4) e de Discover (65).
const FAIXAS_ELO = /^(40117[8-9]|431274|438935|451416|457393|45763[1-2]|506(699|7[0-6][0-9]|77[0-8])|509\d{3}|504175|627780|636297|636368|65003[1-3]|6500(3[5-9]|4[0-9]|5[0-1])|6504(0[5-9]|[1-3][0-9])|650(4[8-9][0-9]|5[0-2][0-9]|53[0-8])|6505(4[1-9]|[5-8][0-9]|9[0-8])|6507(0[0-9]|1[0-8])|65072[0-7]|6509(0[1-9]|1[0-9]|20)|6516(5[2-9]|[6-7][0-9])|6550([0-1][0-9]|2[1-9]|[3-4][0-9]|5[0-8]))/;

export const ehElo = numero => FAIXAS_ELO.test(soDigitos(numero));

// Só para o registro do resultado do 3DS (o servidor aceita estes nomes e mais nada).
export const bandeiraDoCartao = numero => {
    const n = soDigitos(numero);
    if (ehElo(n)) return 'Elo';
    if (/^(606282|3841)/.test(n)) return 'Hipercard';
    if (/^3[47]/.test(n)) return 'Amex';
    if (/^4/.test(n)) return 'Visa';
    if (/^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d\d|27[01]\d|2720)/.test(n)) return 'Master';
    return 'outra';
};

export const cepValido = cep => soDigitos(cep).length === 8;

export const celularValido = tel => [10, 11].includes(soDigitos(tel).length);

// No 3DS o telefone vai com o código do país: 55 + DDD + número.
export const telefoneParaBanco = tel => (soDigitos(tel) ? `55${soDigitos(tel)}` : '');

// Rua e número da Elo. Quando o CEP já trouxe a rua, a pessoa digita só o número.
export const ruaParaBanco = (infoCep, digitado) => {
    const t = `${digitado ?? ''}`.trim();
    if (!t) return '';
    return infoCep && infoCep.rua ? `${infoCep.rua}, ${t}` : t;
};

// O que falta preencher na cobrança do cartão (texto para o aviso), ou null.
export const faltaNaCobranca = ({ cep, infoCep, numero, celular, endereco }) => {
    if (!cepValido(cep)) return 'Preencha o campo: CEP';
    if (infoCep && infoCep.inexistente) return 'CEP não encontrado. Confira o número.';
    if (ehElo(numero)) {
        if (!celularValido(celular)) return 'Preencha o campo: Celular com DDD';
        const minimo = infoCep && infoCep.rua ? 1 : 5;
        if (`${endereco ?? ''}`.trim().length < minimo) {
            return infoCep && infoCep.rua ? 'Preencha o campo: Número' : 'Preencha o campo: Rua e número';
        }
    }
    return null;
};

// Valor em centavos, do jeito que o servidor cobra (plano, ou o valor com cupom).
export const valorEmCentavos = valor => {
    const v = Math.round(Number(valor) * 100);
    return Number.isFinite(v) && v > 0 ? v : 0;
};

export const frequenciaDoPlano = plano => (plano && plano.title === 'Anual' ? 12 : 1);

// Tudo o que o 3DS precisa, tirado do formulário da tela de pagamento. Celular e endereço
// só saem quando o cartão é Elo (quem paga com outro cartão nem vê esses campos).
export const dadosPara3DS = ({ form, email, valor, validade, infoCep }) => {
    const f = form || {};
    const elo = ehElo(f.card_number);
    return {
        numero: soDigitos(f.card_number),
        validade,
        nome: f.card_name,
        email,
        cep: f.cep,
        cidade: infoCep ? infoCep.cidade : undefined,
        uf: infoCep ? infoCep.uf : undefined,
        rua: elo ? ruaParaBanco(infoCep, f.endereco) : undefined,
        celular: elo ? f.celular : undefined,
        valor,
        frequencia: frequenciaDoPlano(f.plan),
    };
};

// Os campos escondidos que o script do 3DS da Cielo lê (classes bpmpi_*), conforme a
// página oficial "Mapear as classes". Campo sem valor não vai.
export const montarCampos3DS = ({ token, pedido, numero, validade, valorCentavos, nome, email,
    cep, cidade, uf, rua, celular, ip, frequencia }) => {
    const [mes, ano] = `${validade ?? ''}`.split('/');
    const campos = {
        bpmpi_auth: 'true',
        bpmpi_accesstoken: token,
        bpmpi_ordernumber: pedido,
        bpmpi_currency: '986',
        bpmpi_totalamount: valorCentavos ? `${valorCentavos}` : '',
        bpmpi_installments: '1',
        bpmpi_paymentmethod: 'Credit',
        bpmpi_cardnumber: soDigitos(numero),
        bpmpi_cardexpirationmonth: mes,
        bpmpi_cardexpirationyear: ano,
        bpmpi_order_productcode: 'PHY',
        bpmpi_order_recurrence: 'true',
        bpmpi_recurring_frequency: `${frequencia || 1}`,
        bpmpi_transaction_mode: 'S',
        bpmpi_merchant_url: 'https://www.joinkids.com.br',
        bpmpi_device_channel: 'Browser',
        bpmpi_billto_contactname: `${nome ?? ''}`.trim().slice(0, 120),
        bpmpi_billto_email: `${email ?? ''}`.trim().slice(0, 255),
        bpmpi_billto_phonenumber: telefoneParaBanco(celular).slice(0, 15),
        bpmpi_billto_street1: `${rua ?? ''}`.trim().slice(0, 60),
        bpmpi_billto_city: `${cidade ?? ''}`.slice(0, 50),
        bpmpi_billto_state: `${uf ?? ''}`.slice(0, 2),
        bpmpi_billto_zipcode: soDigitos(cep).slice(0, 8),
        bpmpi_billto_country: 'BR',
        bpmpi_device_ipaddress: ip,
    };
    return Object.fromEntries(Object.entries(campos).filter(([, v]) => v !== undefined && v !== null && `${v}` !== ''));
};

// Depois do 3DS: a venda segue? Com qual prova? Manual da Cielo: quem diz se autenticou é o
// ECI; se a loja seguir mesmo sem confirmação, o ECI vai junto na venda (e o banco decide).
// Modo "observar": nunca barra. Modo "barrar": para quando o banco diz que não é o dono.
export const decidir3DS = (resultado, modo, bandeira) => {
    const evento = resultado && resultado.evento;
    const d = (resultado && resultado.dados) || {};
    const eci = d.Eci === undefined || d.Eci === null ? '' : `${d.Eci}`;
    const temEci = /^\d{1,2}$/.test(eci);
    const resumo = { evento: evento || 'erroNaTela', eci: temEci ? eci : '-', bandeira: bandeira || 'outra' };
    if (evento === 'onFailure' && modo === 'barrar') return { seguir: false, autenticacao: null, resumo };
    if (temEci && ['onSuccess', 'onFailure', 'onUnenrolled'].includes(evento)) {
        const prova = { Cavv: d.Cavv, Xid: d.Xid, Eci: eci, Version: d.Version, ReferenceId: d.ReferenceId };
        const autenticacao = Object.fromEntries(Object.entries(prova).filter(([, v]) => v !== undefined && v !== null && `${v}` !== ''));
        return { seguir: true, autenticacao, resumo };
    }
    return { seguir: true, autenticacao: null, resumo };
};

// O que vai junto na venda: o resumo (sempre, só para o registro) e a prova (quando houver).
export const camposDo3DSParaVenda = r => (r && r.autenticacao
    ? { resultado_3ds: r.resumo, autenticacao_3ds: r.autenticacao }
    : { resultado_3ds: r ? r.resumo : undefined });

export const MENSAGEM_BANCO_NAO_CONFIRMOU = 'O seu banco não confirmou este pagamento. Tente outro cartão ou pague com PIX.';
